import React, { useState, useEffect } from 'react';
import esgSupplierOnboardingService from '../../../services/esg/esgSupplierOnboardingService';

const ESGSupplierOnboardingProcess = () => {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [filterStage, setFilterStage] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const unsubscribe = esgSupplierOnboardingService.subscribeToOnboardingData((data) => {
      setOnboardingData(data);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>
            Loading ESG Supplier Onboarding Process...
          </p>
        </div>
      </div>
    );
  }

  // Filter suppliers based on current filters
  const filteredSuppliers = onboardingData?.onboardingPipeline?.filter(supplier => {
    const matchesSearch = !searchTerm || 
      supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = filterStage === 'all' || supplier.currentStage === filterStage;
    const matchesRisk = filterRisk === 'all' || supplier.riskLevel === filterRisk;
    
    return matchesSearch && matchesStage && matchesRisk;
  }) || [];

  const handleStageUpdate = (supplierId, newStage) => {
    esgSupplierOnboardingService.updateSupplierStage(supplierId, newStage);
  };

  const handleApproveSupplier = (supplierId) => {
    esgSupplierOnboardingService.approveSupplier(supplierId);
  };

  const handleRejectSupplier = (supplierId, reason) => {
    esgSupplierOnboardingService.rejectSupplier(supplierId, reason || 'Did not meet requirements');
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low': return { background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.15) 100%)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' };
      case 'Medium': return { background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(252, 211, 77, 0.15) 100%)', color: '#f59e0b', border: '1px solid rgba(251, 191, 36, 0.3)' };
      case 'High': return { background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15) 0%, rgba(251, 146, 60, 0.15) 100%)', color: '#f97316', border: '1px solid rgba(249, 115, 22, 0.3)' };
      case 'Critical': return { background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(248, 113, 113, 0.15) 100%)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)' };
      default: return { background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.15) 0%, rgba(156, 163, 175, 0.15) 100%)', color: '#6b7280', border: '1px solid rgba(107, 114, 128, 0.3)' };
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'Initial Application': { background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.15) 100%)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.3)' },
      'Document Collection': { background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(129, 140, 248, 0.15) 100%)', color: '#6366f1', border: '1px solid rgba(99, 102, 241, 0.3)' },
      'ESG Assessment': { background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)', color: '#7c3aed', border: '1px solid rgba(124, 58, 237, 0.3)' },
      'Due Diligence': { background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(244, 114, 182, 0.15) 100%)', color: '#ec4899', border: '1px solid rgba(236, 72, 153, 0.3)' },
      'Risk Evaluation': { background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(248, 113, 113, 0.15) 100%)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)' },
      'Compliance Verification': { background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15) 0%, rgba(251, 146, 60, 0.15) 100%)', color: '#f97316', border: '1px solid rgba(249, 115, 22, 0.3)' },
      'Certification Review': { background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(252, 211, 77, 0.15) 100%)', color: '#f59e0b', border: '1px solid rgba(251, 191, 36, 0.3)' },
      'Contract Negotiation': { background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(74, 222, 128, 0.15) 100%)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)' },
      'Final Approval': { background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.15) 100%)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' },
      'System Integration': { background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15) 0%, rgba(45, 212, 191, 0.15) 100%)', color: '#14b8a6', border: '1px solid rgba(20, 184, 166, 0.3)' }
    };
    return colors[stage] || { background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.15) 0%, rgba(156, 163, 175, 0.15) 100%)', color: '#6b7280', border: '1px solid rgba(107, 114, 128, 0.3)' };
  };

  const refreshData = () => {
    setLoading(true);
    setTimeout(() => {
      esgSupplierOnboardingService.refreshOnboardingData();
      setLoading(false);
    }, 500);
  };

  const renderPipelineTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Controls */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4b5563', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search suppliers..."
                style={{
                  padding: '10px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '14px',
                  minWidth: '250px',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4b5563', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stage</label>
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                style={{
                  padding: '10px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '14px',
                  minWidth: '180px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Stages</option>
                {onboardingData?.stageMetrics?.map(stage => (
                  <option key={stage.stage} value={stage.stage}>{stage.stage}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4b5563', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Risk Level</label>
              <select
                value={filterRisk}
                onChange={(e) => setFilterRisk(e.target.value)}
                style={{
                  padding: '10px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '14px',
                  minWidth: '180px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Risk Levels</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b7280',
            padding: '10px 16px',
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
            borderRadius: '10px'
          }}>
            Showing <span style={{ color: '#7c3aed', fontWeight: '700' }}>{filteredSuppliers.length}</span> of {onboardingData?.onboardingPipeline?.length || 0} suppliers
          </div>
        </div>
      </div>

      {/* Stage Overview */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '20px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Onboarding Stage Overview
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          {onboardingData?.stageMetrics?.map(stage => (
            <div key={stage.stage} style={{
              textAlign: 'center',
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
              borderRadius: '12px',
              border: '2px solid rgba(124, 58, 237, 0.2)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(124, 58, 237, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{
                fontSize: '36px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 8px rgba(124, 58, 237, 0.3)',
                marginBottom: '8px'
              }}>
                {stage.count}
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '700',
                color: '#4b5563',
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {stage.stage}
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#7c3aed'
              }}>
                {stage.percentage}%
              </div>
              {stage.bottleneck && (
                <div style={{ marginTop: '8px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                  }}>
                    ⚠️ Bottleneck
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Suppliers List */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '2px solid #f3f4f6',
          background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '800',
            color: '#111827',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            📋 Supplier Onboarding Pipeline
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Supplier</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Stage</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Progress</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Risk Level</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>ESG Score</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Assigned To</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier, idx) => (
                <tr key={supplier.id} style={{
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>{supplier.name}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{supplier.id} • {supplier.category}</div>
                    </div>
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      ...getStageColor(supplier.currentStage)
                    }}>
                      {supplier.currentStage}
                    </span>
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <div style={{
                      width: '120px',
                      height: '8px',
                      background: '#e5e7eb',
                      borderRadius: '10px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${supplier.progress}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)',
                        borderRadius: '10px',
                        transition: 'width 0.3s ease',
                        boxShadow: '0 0 8px rgba(124, 58, 237, 0.4)'
                      }} />
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontWeight: '600' }}>{supplier.progress}%</div>
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      ...getRiskColor(supplier.riskLevel)
                    }}>
                      {supplier.riskLevel}
                    </span>
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>{supplier.esgScore}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>out of 100</div>
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap', fontSize: '14px', color: '#111827', fontWeight: '600' }}>
                    {supplier.assignedTo}
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button
                      onClick={() => setSelectedSupplier(supplier)}
                      style={{
                        color: '#7c3aed',
                        fontWeight: '600',
                        fontSize: '13px',
                        marginRight: '12px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#5b21b6'}
                      onMouseLeave={(e) => e.target.style.color = '#7c3aed'}
                    >
                      View Details
                    </button>
                    {supplier.progress >= 90 && (
                      <button
                        onClick={() => handleApproveSupplier(supplier.id)}
                        style={{
                          color: '#10b981',
                          fontWeight: '600',
                          fontSize: '13px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#047857'}
                        onMouseLeave={(e) => e.target.style.color = '#10b981'}
                      >
                        Approve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderDueDiligenceTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '24px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          🔍 Due Diligence Templates
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
          {onboardingData?.dueDiligenceTemplates?.map(template => (
            <div key={template.id} style={{
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(249, 250, 251, 1) 100%)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(124, 58, 237, 0.15)';
              e.currentTarget.style.borderColor = '#7c3aed';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#111827',
                  flex: 1
                }}>
                  {template.name}
                </h4>
                <span style={{
                  fontSize: '11px',
                  background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)',
                  color: '#7c3aed',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  border: '1px solid rgba(124, 58, 237, 0.3)'
                }}>
                  {template.category}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'rgba(59, 130, 246, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#4b5563', fontWeight: '600' }}>📋 Questions:</span>
                  <span style={{ fontSize: '14px', color: '#3b82f6', fontWeight: '700' }}>{template.questions}</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'rgba(168, 85, 247, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(168, 85, 247, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#4b5563', fontWeight: '600' }}>⏱️ Est. Time:</span>
                  <span style={{ fontSize: '14px', color: '#a855f7', fontWeight: '700' }}>{template.completionTime}</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'rgba(16, 185, 129, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#4b5563', fontWeight: '600' }}>🏢 Frameworks:</span>
                  <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '700' }}>{template.frameworks.join(', ')}</span>
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  ⚠️ Critical Items:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {template.criticalItems.map(item => (
                    <span key={item} style={{
                      fontSize: '11px',
                      background: '#f3f4f6',
                      color: '#374151',
                      padding: '6px 12px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      border: '1px solid #e5e7eb'
                    }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <button style={{
                width: '100%',
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                color: 'white',
                padding: '12px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '700',
                border: 'none',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.02)';
                e.target.style.boxShadow = '0 6px 16px rgba(124, 58, 237, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
              }}>
                Use Template
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderRiskAssessmentTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '24px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          ⚠️ Risk Assessment Matrix
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)' }}>
              <tr>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Risk Category</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Impact</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Probability</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Risk Score</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Mitigation</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Owner</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {onboardingData?.riskMatrix?.map((risk, index) => (
                <tr key={index} style={{
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                    {risk.category}
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      ...(risk.impact === 'Critical' ? { background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', color: 'white', boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)' } :
                         risk.impact === 'High' ? { background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', color: 'white', boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)' } :
                         risk.impact === 'Medium' ? { background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', color: 'white', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)' } :
                         { background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', color: 'white', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' })
                    }}>
                      {risk.impact}
                    </span>
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {risk.probability}
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 14px',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '800',
                      ...(risk.score >= 12 ? { background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', color: 'white', boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)' } :
                         risk.score >= 8 ? { background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', color: 'white', boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)' } :
                         risk.score >= 4 ? { background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', color: 'white', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)' } :
                         { background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', color: 'white', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' })
                    }}>
                      {risk.score}
                    </div>
                  </td>
                  <td style={{ padding: '16px 24px', fontSize: '13px', color: '#4b5563', maxWidth: '300px', fontWeight: '500' }}>
                    {risk.mitigation}
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {risk.owner}
                  </td>
                  <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      ...(risk.status === 'Closed' ? { background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', color: 'white', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' } :
                         risk.status === 'Mitigated' ? { background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', color: 'white', boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)' } :
                         risk.status === 'In Progress' ? { background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', color: 'white', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)' } :
                         { background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', color: 'white', boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)' })
                    }}>
                      {risk.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderComplianceTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '24px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          ✅ ESG Compliance Framework Status
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '24px' }}>
          {onboardingData?.complianceChecklist?.map((framework, index) => (
            <div key={index} style={{
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(249, 250, 251, 1) 100%)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(16, 185, 129, 0.15)';
              e.currentTarget.style.borderColor = '#10b981';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#111827',
                  flex: 1
                }}>
                  {framework.framework}
                </h4>
                <span style={{
                  fontSize: '11px',
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...(framework.status === 'Compliant' ? { background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', color: 'white', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' } :
                     framework.status === 'In Progress' ? { background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', color: 'white', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)' } :
                     { background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', color: 'white', boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)' })
                }}>
                  {framework.status}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'rgba(124, 58, 237, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(124, 58, 237, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>📊 Score:</span>
                  <span style={{ fontSize: '16px', color: '#7c3aed', fontWeight: '800' }}>{framework.score}/100</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'rgba(59, 130, 246, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>📅 Last Assessment:</span>
                  <span style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '700' }}>{framework.lastAssessment}</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'rgba(16, 185, 129, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>🔄 Next Review:</span>
                  <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '700' }}>{framework.nextReview}</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: framework.criticalItems > 0 ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                  borderRadius: '10px',
                  border: framework.criticalItems > 0 ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(16, 185, 129, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>⚠️ Critical Items:</span>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '800',
                    color: framework.criticalItems > 0 ? '#ef4444' : '#10b981'
                  }}>
                    {framework.criticalItems}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: framework.documentation === 'Complete' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                  borderRadius: '10px',
                  border: framework.documentation === 'Complete' ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(245, 158, 11, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>📄 Documentation:</span>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '700',
                    color: framework.documentation === 'Complete' ? '#10b981' : '#f59e0b'
                  }}>
                    {framework.documentation}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'rgba(107, 114, 128, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(107, 114, 128, 0.2)'
                }}>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>👤 Auditor:</span>
                  <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>{framework.auditor}</span>
                </div>
              </div>
              {framework.certificationRequired && (
                <div style={{
                  fontSize: '12px',
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.15) 100%)',
                  color: '#3b82f6',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  fontWeight: '700',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}>
                  🎓 Certification Required
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Document Categories */}
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '24px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          📂 Document Categories
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {onboardingData?.documentCategories?.map((category, index) => (
            <div key={index} style={{
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(249, 250, 251, 1) 100%)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(124, 58, 237, 0.12)';
              e.currentTarget.style.borderColor = '#7c3aed';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827'
                }}>
                  {category.category}
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '140px',
                    height: '10px',
                    background: '#e5e7eb',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${category.completionRate}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)',
                      borderRadius: '10px',
                      transition: 'width 0.3s ease',
                      boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)'
                    }} />
                  </div>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '800',
                    color: '#7c3aed'
                  }}>
                    {category.completionRate}%
                  </span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                <div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>✅</span> Required Documents:
                  </div>
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: 0, listStyle: 'none' }}>
                    {category.required.map(doc => (
                      <li key={doc} style={{
                        fontSize: '13px',
                        color: '#4b5563',
                        fontWeight: '600',
                        padding: '8px 12px',
                        background: 'rgba(16, 185, 129, 0.08)',
                        borderRadius: '8px',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ color: '#10b981', fontSize: '14px' }}>●</span> {doc}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>📎</span> Optional Documents:
                  </div>
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: 0, listStyle: 'none' }}>
                    {category.optional.map(doc => (
                      <li key={doc} style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        fontWeight: '500',
                        padding: '8px 12px',
                        background: 'rgba(107, 114, 128, 0.06)',
                        borderRadius: '8px',
                        border: '1px solid rgba(107, 114, 128, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ color: '#9ca3af', fontSize: '14px' }}>○</span> {doc}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAutomationTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '24px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          🤖 Workflow Automation Rules
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {onboardingData?.automationRules?.map(rule => (
            <div key={rule.id} style={{
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(249, 250, 251, 1) 100%)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(124, 58, 237, 0.12)';
              e.currentTarget.style.borderColor = '#7c3aed';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    {rule.name}
                  </h4>
                  <p style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontWeight: '600'
                  }}>
                    {rule.id}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '6px 14px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    ...(rule.enabled ? { background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', color: 'white', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' } :
                       { background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)', color: 'white', boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)' })
                  }}>
                    {rule.enabled ? '✓ Enabled' : '○ Disabled'}
                  </span>
                  <button
                    onClick={() => esgSupplierOnboardingService.toggleAutomationRule(rule.id)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: '700',
                      border: 'none',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      transition: 'all 0.3s ease',
                      ...(rule.enabled ?
                         { background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(248, 113, 113, 0.15) 100%)', color: '#ef4444', border: '2px solid rgba(239, 68, 68, 0.3)' } :
                         { background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.15) 100%)', color: '#10b981', border: '2px solid rgba(16, 185, 129, 0.3)' })
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = rule.enabled ? '0 4px 12px rgba(239, 68, 68, 0.3)' : '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {rule.enabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                <div style={{
                  padding: '16px',
                  background: 'rgba(59, 130, 246, 0.08)',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#3b82f6',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    ⚡ Trigger:
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#1f2937',
                    fontWeight: '600'
                  }}>
                    {rule.trigger}
                  </div>
                </div>
                <div style={{
                  padding: '16px',
                  background: 'rgba(168, 85, 247, 0.08)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#a855f7',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    🎯 Condition:
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#1f2937',
                    fontWeight: '600'
                  }}>
                    {rule.condition}
                  </div>
                </div>
                <div style={{
                  padding: '16px',
                  background: 'rgba(16, 185, 129, 0.08)',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#10b981',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    🚀 Action:
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#1f2937',
                    fontWeight: '600'
                  }}>
                    {rule.action}
                  </div>
                </div>
              </div>
              {rule.lastTriggered && (
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  fontWeight: '600',
                  padding: '10px 14px',
                  background: 'rgba(107, 114, 128, 0.06)',
                  borderRadius: '8px',
                  border: '1px solid rgba(107, 114, 128, 0.15)'
                }}>
                  ⏰ Last triggered: {rule.lastTriggered}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics */}
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '24px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          📊 Performance Metrics
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
            borderRadius: '16px',
            border: '2px solid rgba(124, 58, 237, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(124, 58, 237, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(124, 58, 237, 0.3)'
            }}>
              {onboardingData?.performanceMetrics?.averageOnboardingTime}
            </div>
            <div style={{
              fontSize: '13px',
              color: '#6b7280',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Avg. Days
            </div>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(52, 211, 153, 0.08) 100%)',
            borderRadius: '16px',
            border: '2px solid rgba(16, 185, 129, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
            }}>
              {onboardingData?.performanceMetrics?.onboardingSuccess}%
            </div>
            <div style={{
              fontSize: '13px',
              color: '#6b7280',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Success Rate
            </div>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(96, 165, 250, 0.08) 100%)',
            borderRadius: '16px',
            border: '2px solid rgba(59, 130, 246, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
            }}>
              {onboardingData?.performanceMetrics?.averageEsgScore}
            </div>
            <div style={{
              fontSize: '13px',
              color: '#6b7280',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Avg. ESG Score
            </div>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.08) 0%, rgba(251, 146, 60, 0.08) 100%)',
            borderRadius: '16px',
            border: '2px solid rgba(249, 115, 22, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(249, 115, 22, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
            }}>
              {onboardingData?.performanceMetrics?.complianceRate}%
            </div>
            <div style={{
              fontSize: '13px',
              color: '#6b7280',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Compliance Rate
            </div>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(248, 113, 113, 0.08) 100%)',
            borderRadius: '16px',
            border: '2px solid rgba(239, 68, 68, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
            }}>
              ${onboardingData?.performanceMetrics?.costPerOnboarding}
            </div>
            <div style={{
              fontSize: '13px',
              color: '#6b7280',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Cost per Onboarding
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'pipeline', label: 'Onboarding Pipeline', icon: '📋' },
    { id: 'duediligence', label: 'Due Diligence', icon: '🔍' },
    { id: 'risk', label: 'Risk Assessment', icon: '⚠️' },
    { id: 'compliance', label: 'Compliance', icon: '✅' },
    { id: 'automation', label: 'Automation', icon: '🤖' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Pattern Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.1
        }} />

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '30px', marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
              }}>
                📋
              </div>
              <div>
                <h1 style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: 'white',
                  margin: '0 0 8px 0',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  letterSpacing: '-0.5px'
                }}>
                  ESG Supplier Onboarding Process
                </h1>
                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.95)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Comprehensive supplier onboarding with ESG compliance verification, risk assessment, and due diligence automation
                </p>
              </div>
            </div>

            <button
              onClick={refreshData}
              style={{
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
            >
              <span style={{ fontSize: '16px' }}>🔄</span>
              Refresh Data
            </button>
          </div>

          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div style={{
              padding: '20px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Total Suppliers
              </div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: 'white', textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>
                {onboardingData?.onboardingPipeline?.length || 0}
              </div>
            </div>

            <div style={{
              padding: '20px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Avg. Onboarding Days
              </div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: 'white', textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>
                {onboardingData?.performanceMetrics?.averageOnboardingTime || 0}
              </div>
            </div>

            <div style={{
              padding: '20px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Success Rate
              </div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: 'white', textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>
                {onboardingData?.performanceMetrics?.onboardingSuccess || 0}%
              </div>
            </div>

            <div style={{
              padding: '20px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Avg. ESG Score
              </div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: 'white', textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>
                {onboardingData?.performanceMetrics?.averageEsgScore || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '0', padding: '0 20px' }}>
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: index < 3 ? '1' : 'none',
                minWidth: index >= 3 ? '200px' : 'auto',
                padding: '16px 20px',
                background: activeTab === tab.id ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #7c3aed' : '3px solid transparent',
                color: activeTab === tab.id ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '700' : '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Container */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Tab Content */}
        <div>
          {activeTab === 'pipeline' && renderPipelineTab()}
          {activeTab === 'duediligence' && renderDueDiligenceTab()}
          {activeTab === 'risk' && renderRiskAssessmentTab()}
          {activeTab === 'compliance' && renderComplianceTab()}
          {activeTab === 'automation' && renderAutomationTab()}
        </div>
      </div>

      {/* Supplier Detail Modal */}
      {selectedSupplier && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          overflowY: 'auto',
          height: '100%',
          width: '100%',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '1000px',
            background: 'white',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '2px solid rgba(124, 58, 237, 0.2)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              padding: '24px 32px',
              borderRadius: '22px 22px 0 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: 'white',
                margin: 0,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}>
                🏭 Supplier Details: {selectedSupplier.name}
              </h3>
              <button
                onClick={() => setSelectedSupplier(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  color: 'white',
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: '700',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '32px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
                {/* Left Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Basic Information */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(96, 165, 250, 0.08) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📋 Basic Information
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'ID', value: selectedSupplier.id },
                        { label: 'Category', value: selectedSupplier.category },
                        { label: 'Region', value: selectedSupplier.region },
                        { label: 'Priority', value: selectedSupplier.priority }
                      ].map(item => (
                        <div key={item.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '8px'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>{item.label}:</span>
                          <span style={{ fontWeight: '700', color: '#111827' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(192, 132, 252, 0.08) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(168, 85, 247, 0.2)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📞 Contact Information
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'Contact', value: selectedSupplier.contactInfo.primaryContact },
                        { label: 'Email', value: selectedSupplier.contactInfo.email },
                        { label: 'Phone', value: selectedSupplier.contactInfo.phone },
                        { label: 'Role', value: selectedSupplier.contactInfo.role }
                      ].map(item => (
                        <div key={item.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '8px'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>{item.label}:</span>
                          <span style={{ fontWeight: '700', color: '#111827' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Business Information */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(52, 211, 153, 0.08) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      🏢 Business Information
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'Founded', value: selectedSupplier.businessInfo.founded },
                        { label: 'Employees', value: selectedSupplier.businessInfo.employees },
                        { label: 'Revenue', value: selectedSupplier.businessInfo.revenue },
                        { label: 'Ownership', value: selectedSupplier.businessInfo.ownership }
                      ].map(item => (
                        <div key={item.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '8px'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>{item.label}:</span>
                          <span style={{ fontWeight: '700', color: '#111827' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Onboarding Status */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(251, 191, 36, 0.08) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(245, 158, 11, 0.2)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📊 Onboarding Status
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'Current Stage', value: selectedSupplier.currentStage },
                        { label: 'Progress', value: `${selectedSupplier.progress}%` },
                        { label: 'Days in Process', value: selectedSupplier.daysInProcess },
                        { label: 'Expected Completion', value: selectedSupplier.expectedCompletion }
                      ].map(item => (
                        <div key={item.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '8px'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>{item.label}:</span>
                          <span style={{ fontWeight: '700', color: '#111827' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ESG & Compliance */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(52, 211, 153, 0.08) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      🌱 ESG & Compliance
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'ESG Score', value: `${selectedSupplier.esgScore}/100` },
                        { label: 'Risk Level', value: selectedSupplier.riskLevel },
                        { label: 'Compliance Status', value: selectedSupplier.complianceStatus }
                      ].map(item => (
                        <div key={item.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '8px'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>{item.label}:</span>
                          <span style={{ fontWeight: '700', color: '#111827' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Documents */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(96, 165, 250, 0.08) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📄 Documents
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'Total', value: selectedSupplier.documents.total },
                        { label: 'Submitted', value: selectedSupplier.documents.submitted },
                        { label: 'Approved', value: selectedSupplier.documents.approved },
                        { label: 'Missing', value: selectedSupplier.documents.missing }
                      ].map(item => (
                        <div key={item.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '8px'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>{item.label}:</span>
                          <span style={{ fontWeight: '700', color: '#111827' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Certifications */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(52, 211, 153, 0.08) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      🎓 Certifications
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedSupplier.certifications.map(cert => (
                        <span key={cert} style={{
                          fontSize: '11px',
                          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '12px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                        }}>
                          ✓ {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '24px 32px',
              borderTop: '2px solid #f3f4f6',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              position: 'sticky',
              bottom: 0,
              background: 'white',
              borderRadius: '0 0 22px 22px'
            }}>
              <button
                onClick={() => setSelectedSupplier(null)}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#374151',
                  background: 'white',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f9fafb';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                Close
              </button>
              {selectedSupplier.progress >= 90 && (
                <button
                  onClick={() => {
                    handleApproveSupplier(selectedSupplier.id);
                    setSelectedSupplier(null);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: 'white',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  ✓ Approve Supplier
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ESGSupplierOnboardingProcess;